// The sandboxes' pages outside /me, put next to cloud's own routes:
//
// - /preview-auth?host=&path=: the preview relay sends a viewer without its
//   cookie here. Signed in (else /signin and back), the screen gets a
//   one-minute ticket for that host and goes on to the relay with it. The
//   ticket opens once, so it is fetched each time and never cached.
// - /previews: kept as an address; the list is an item of Me's settings
//   (/me/settings/previews, previews.tsx).
// The operator's allowances are an item of /admin (admin.tsx).

import { type AnyRoute, createRoute, redirect } from "@tanstack/react-router";
import { m } from "@trameria/cloud-i18n";
import { ApiError } from "@trameria/ui-core";
import { useEffect } from "react";
import { type Ctx, requireApproved } from "../routes";
import type { CloudSession } from "../session";
import { previewTicket } from "./queries";

const text = (v: unknown) => (typeof v === "string" ? v : "");

export function sandboxRoutes(
  root: AnyRoute,
  session: CloudSession,
): AnyRoute[] {
  const previews = createRoute({
    getParentRoute: () => root,
    path: "previews",
    beforeLoad: () => {
      throw redirect({ to: "/me/settings/previews", replace: true });
    },
  });

  const previewAuth = createRoute({
    getParentRoute: () => root,
    path: "preview-auth",
    validateSearch: (s: Record<string, unknown>) => ({
      host: text(s.host),
      path: text(s.path) || "/",
    }),
    beforeLoad: async ({ context, location }) => {
      await requireApproved(context as Ctx, session, location.href);
    },
    loaderDeps: ({ search }) => search,
    loader: ({ context, deps }) => previewTicket((context as Ctx).api, deps),
    gcTime: 0,
    staleTime: 0,
    component: function PreviewAuth() {
      const { url } = previewAuth.useLoaderData();
      // frontend-development: going on to the relay is a write-only side effect (not a fetch)
      useEffect(() => {
        window.location.replace(url);
      }, [url]);
      return <PreviewAuthLine>{m.sandboxes_preview_opening()}</PreviewAuthLine>;
    },
    errorComponent: function PreviewAuthError({ error }: { error: unknown }) {
      const forbidden = error instanceof ApiError && error.status === 403;
      return (
        <PreviewAuthLine>
          {forbidden
            ? m.sandboxes_preview_forbidden()
            : m.sandboxes_preview_open_failed()}
        </PreviewAuthLine>
      );
    },
  });

  return [previews, previewAuth];
}

function PreviewAuthLine({ children }: { children: string }) {
  return (
    <div className="flex h-dvh items-start bg-surface p-6 text-[13px] text-muted">
      <p>{children}</p>
    </div>
  );
}
